import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Archive,
  Calendar,
  Users,
  FileText,
  Clock,
  Youtube,
  Mic,
  ArrowRight,
  Loader2,
  Hash,
  EyeOff,
  Trash2,
  Share2,
  Filter,
  Globe,
  MessageSquare,
  Film,
  Scissors,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Undo2
} from 'lucide-react';
import {
  sessionService,
  listArrangements,
  getArrangementsStorageSummary,
  deleteArrangement,
  purgeArrangementCache,
  ArrangementItem,
  ArrangementStorageSummaryItem,
  SessionStorageSummary
} from '../services/sessionService';
import { useAuth } from '../services/AuthContext';
import { useDarkMode } from '../contexts/DarkModeContext';
import { ConfirmationModal } from './ConfirmationModal';
import DropboxIcon from './DropboxIcon';

interface SessionItem {
  sessionId: string;
  filename: string;
  title: string;
  showSlug: string;
  showTitle: string;
  family?: {
    slug: string;
    title: string | null;
    versionNumber: number | null;
    versionCount: number;
    headSessionId: string | null;
    isHead: boolean;
  } | null;
  contentSource: 'owned' | 'external';
  contentVisibility: 'default' | 'internal';
  tags: string[];
  isYouTubeMode: boolean;
  youtubeUrl?: string;
  videoUrl?: string;
  videoSource?: 'youtube' | 'dropbox' | 'file' | null;
  speakerCount: number;
  speakerNames: string[];
  wordCount: number;
  shareLinksCount: number;
  publishedToHomepage: boolean;
  isDeprecated: boolean;
  deprecatedAt?: string | null;
  deprecatedReason?: string | null;
  createdAt: Date;
  updatedAt: Date;
  timeAgo: string;
}

type ArrangementListItem = ArrangementItem & Partial<ArrangementStorageSummaryItem>;

type FilterType = 'all' | 'youtube' | 'video' | 'shared' | 'deprecated';
type ContentSourceFilter = 'owned' | 'external' | 'all';
type ContentVisibilityFilter = 'default' | 'internal' | 'all';

interface SessionStorageDigest {
  sourceBytes: number;
  proxyBytes: number;
  totalBytes: number;
  estimatedMonthlyStorageCostUsd: number;
  playableCount: number;
  cleanupCandidateCount: number;
  cleanupCandidateBytes: number;
  hasPlayableVideo: boolean;
  hasYoutubeVideo: boolean;
  hasProxyVideo: boolean;
  hasAwsSource: boolean;
}

interface SessionBrowserProps {
  onSessionSelect: (sessionId: string) => void;
  className?: string;
}

const SessionBrowser: React.FC<SessionBrowserProps> = ({ onSessionSelect, className = '' }) => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { isDarkMode } = useDarkMode();
  const [sessions, setSessions] = useState<SessionItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<SessionItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deprecationModalOpen, setDeprecationModalOpen] = useState(false);
  const [sessionToToggleDeprecation, setSessionToToggleDeprecation] = useState<SessionItem | null>(null);
  const [isTogglingDeprecation, setIsTogglingDeprecation] = useState(false);
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [showFilter, setShowFilter] = useState<string>('all');
  const [contentSourceFilter, setContentSourceFilter] = useState<ContentSourceFilter>('owned');
  const [visibilityFilter, setVisibilityFilter] = useState<ContentVisibilityFilter>('all');
  const [togglingPublish, setTogglingPublish] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'sessions' | 'arrangements'>('sessions');
  const [arrangements, setArrangements] = useState<ArrangementListItem[]>([]);
  const [isLoadingArrangements, setIsLoadingArrangements] = useState(false);
  const [arrangementCacheModalOpen, setArrangementCacheModalOpen] = useState(false);
  const [arrangementDeleteModalOpen, setArrangementDeleteModalOpen] = useState(false);
  const [arrangementToDelete, setArrangementToDelete] = useState<ArrangementListItem | null>(null);
  const [isPurgingArrangementCache, setIsPurgingArrangementCache] = useState(false);
  const [isDeletingArrangement, setIsDeletingArrangement] = useState(false);
  const [expandedFamilies, setExpandedFamilies] = useState<Set<string>>(new Set());
  const [sessionStorageSummary, setSessionStorageSummary] = useState<SessionStorageSummary | null>(null);

  const shellClassName = `overflow-hidden rounded-[30px] border border-[#D6DCE4] bg-[linear-gradient(180deg,rgba(255,255,255,0.9),rgba(248,248,247,0.8))] shadow-[0_34px_72px_-40px_rgba(35,46,63,0.45)] backdrop-blur-2xl dark:border-[#344052] dark:bg-[linear-gradient(180deg,rgba(27,36,50,0.92),rgba(20,28,40,0.82))] ${className}`;
  const itemCardClassName = 'rounded-[24px] border border-[#D6DCE4] bg-white/[0.72] p-4 shadow-[0_20px_40px_-28px_rgba(35,46,63,0.34)] backdrop-blur-xl transition-all duration-200 hover:-translate-y-0.5 hover:border-[#FFB52B]/40 hover:bg-[#FFF8EA]/[0.88] hover:shadow-[0_30px_52px_-28px_rgba(35,46,63,0.4)] dark:border-[#415067] dark:bg-[linear-gradient(180deg,rgba(33,45,62,0.94),rgba(24,34,49,0.94))] dark:shadow-[0_26px_48px_-30px_rgba(0,0,0,0.68)] dark:hover:border-[#FFB52B]/[0.42] dark:hover:bg-[linear-gradient(180deg,rgba(38,52,71,0.98),rgba(28,40,56,0.98))]';
  const subtleCardClassName = 'rounded-[24px] border border-[#D6DCE4] bg-white/80 shadow-[0_20px_40px_-28px_rgba(35,46,63,0.3)] dark:border-[#344052] dark:bg-[#202A3A]/80';
  const pillButtonBaseClassName = 'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 font-headlines text-xs font-semibold uppercase tracking-[0.12em] transition-all duration-200';
  const browserHeaderClassName = isDarkMode
    ? 'sticky top-0 z-20 border-[#344052] bg-[#232E3F]/85 text-[#F4F4F4] backdrop-blur-2xl'
    : 'sticky top-0 z-20 border-[#D6DCE4] bg-[linear-gradient(180deg,rgba(255,255,255,0.84),rgba(244,244,244,0.84))] text-[#232E3F] backdrop-blur-2xl';
  const inactivePillClassName = isDarkMode
    ? 'border-white/[0.12] bg-white/[0.08] text-white/[0.78] hover:bg-white/[0.14] hover:text-white'
    : 'border-[#D6DCE4] bg-white/[0.9] text-[#4A586E] hover:bg-[#232E3F]/[0.05] hover:text-[#232E3F]';
  const activePillClassName = 'border-[#FFB52B]/40 bg-[#FFB52B] text-[#232E3F] shadow-[0_18px_32px_-24px_rgba(255,181,43,0.85)]';
  const headerEyebrowClassName = isDarkMode ? 'text-[#FFB52B]/[0.78]' : 'text-[#A36A00]';
  const headerTitleClassName = isDarkMode ? 'text-[#F4F4F4]' : 'text-[#232E3F]';
  const headerBodyClassName = isDarkMode ? 'text-[#D5D9E0]' : 'text-[#566377]';
  const viewToggleClassName = isDarkMode
    ? 'border border-white/[0.12] bg-white/[0.08]'
    : 'border border-[#D6DCE4] bg-white/[0.88]';
  const inactiveViewModeClassName = isDarkMode
    ? 'text-white/[0.72] hover:bg-white/[0.08] hover:text-white'
    : 'text-[#4A586E] hover:bg-[#232E3F]/[0.05] hover:text-[#232E3F]';
  const searchInputClassName = isDarkMode
    ? 'w-full rounded-2xl border border-white/[0.12] bg-white/[0.08] py-3 pl-12 pr-4 font-body text-sm text-[#F4F4F4] placeholder:text-white/[0.45] transition-all duration-200 focus:border-[#FFB52B]/[0.45] focus:bg-white/[0.1] focus:outline-none focus:ring-2 focus:ring-[#FFB52B]/20'
    : 'w-full rounded-2xl border border-[#D6DCE4] bg-white/[0.92] py-3 pl-12 pr-4 font-body text-sm text-[#232E3F] placeholder:text-[#6B788B] transition-all duration-200 focus:border-[#FFB52B]/[0.5] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#FFB52B]/20';
  const selectInputClassName = isDarkMode
    ? 'rounded-2xl border border-white/[0.12] bg-white/[0.08] px-4 py-3 font-body text-sm text-[#F4F4F4] transition-all duration-200 focus:border-[#FFB52B]/[0.45] focus:bg-white/[0.1] focus:outline-none focus:ring-2 focus:ring-[#FFB52B]/20'
    : 'rounded-2xl border border-[#D6DCE4] bg-white/[0.92] px-4 py-3 font-body text-sm text-[#232E3F] transition-all duration-200 focus:border-[#FFB52B]/[0.5] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#FFB52B]/20';
  const searchIconClassName = isDarkMode ? 'text-white/[0.58]' : 'text-[#7B8798]';
  const filterIconClassName = isDarkMode ? 'text-white/[0.55]' : 'text-[#7B8798]';
  const sessionChipBaseClassName = 'rounded-full px-2.5 py-1 font-headlines text-[11px] font-semibold';
  const sessionChipClassNames = {
    version: `${sessionChipBaseClassName} uppercase tracking-[0.1em] bg-[#EAF7EF] text-[#2D7250] dark:bg-emerald-950/40 dark:text-emerald-300`,
    count: `${sessionChipBaseClassName} uppercase tracking-[0.1em] bg-[#EEF3FF] text-[#355E9A] dark:bg-blue-950/35 dark:text-blue-200`,
    gold: `${sessionChipBaseClassName} uppercase tracking-[0.1em] border border-[#F2D9A6] bg-[#FFF4D9] text-[#8A6100] dark:border-brand-yellow/25 dark:bg-brand-yellow/15 dark:text-brand-yellow`,
    danger: `${sessionChipBaseClassName} uppercase tracking-[0.1em] bg-[#FDE8E8] text-[#B54747] dark:bg-red-950/40 dark:text-red-300`,
    show: `${sessionChipBaseClassName} uppercase tracking-[0.1em] bg-[#F1EFFA] text-[#5E548E] dark:bg-violet-950/35 dark:text-violet-200`,
    external: `${sessionChipBaseClassName} uppercase tracking-[0.1em] bg-[#E6F2FF] text-[#225C9C] dark:bg-blue-950/40 dark:text-blue-200`,
    neutral: `${sessionChipBaseClassName} uppercase tracking-[0.1em] bg-[#232E3F]/[0.08] text-[#566377] dark:bg-brand-navy/30 dark:text-brand-cloud`,
    speaker: `${sessionChipBaseClassName} border border-[#F2D9A6] bg-[#FFF4D9] text-[#8A6100] dark:border-brand-yellow/25 dark:bg-brand-yellow/15 dark:text-brand-yellow`,
    more: `${sessionChipBaseClassName} bg-[#E8ECF1] text-[#566377] dark:bg-brand-navy/30 dark:text-brand-cloud`,
    tag: `${sessionChipBaseClassName} bg-[#EAF7EF] text-[#2D7250] dark:bg-emerald-950/35 dark:text-emerald-300`,
  } as const;

  const getFilterButtonClassName = (filter: FilterType): string =>
    `${pillButtonBaseClassName} ${activeFilter === filter ? activePillClassName : inactivePillClassName}`;
  const EmptySessionIcon = activeFilter !== 'all' ? Filter : FileText;
  const emptySessionTitle = activeFilter !== 'all'
    ? 'No matching sessions'
    : searchTerm
      ? 'No sessions found'
      : 'No sessions yet';
  const emptySessionDescription = activeFilter !== 'all'
    ? activeFilter === 'deprecated'
      ? 'No deprecated sessions are currently hidden from the main list.'
      : 'No sessions match the current filter. Try selecting a different filter.'
    : searchTerm
      ? 'Try adjusting your search terms.'
      : 'Start by transcribing your first audio file or YouTube video.';

  const loadArrangements = useCallback(async () => {
    if (!token) return;
    setIsLoadingArrangements(true);
    try {
      const items = await listArrangements(token);
      setArrangements(items);

      getArrangementsStorageSummary(token)
        .then(summary => {
          const storageById = new Map((summary?.arrangements || []).map(item => [item.id, item]));
          setArrangements(current => current.map(arr => ({ ...arr, ...(storageById.get(arr.id) || {}) })));
        })
        .catch(err => {
          console.error('Failed to load arrangement storage summary:', err);
        });
    } finally {
      setIsLoadingArrangements(false);
    }
  }, [token]);

  const loadSessions = useCallback(async (search?: string, filter?: FilterType) => {
    if (!token) return;

    try {
      setIsSearching(
        !!search
        || filter !== 'all'
        || showFilter !== 'all'
        || contentSourceFilter !== 'owned'
        || visibilityFilter !== 'all'
      );
      const sessionList = await sessionService.listSessions(token, search, 50, filter, {
        show: showFilter,
        contentSource: contentSourceFilter,
        contentVisibility: visibilityFilter,
        includeExternal: contentSourceFilter === 'all' || contentSourceFilter === 'external',
      });
      setSessions(sessionList);
      setError(null);
    } catch (err) {
      console.error('Failed to load sessions:', err);
      setError('Failed to load sessions');
    } finally {
      setIsLoading(false);
      setIsSearching(false);
    }
  }, [token, showFilter, contentSourceFilter, visibilityFilter]);

  const loadSessionStorageSummary = useCallback(async (search?: string, filter?: FilterType) => {
    if (!token) return;

    try {
      const summary = await sessionService.getSessionStorageSummary(token, search, filter, {
        show: showFilter,
        contentSource: contentSourceFilter,
        contentVisibility: visibilityFilter,
        includeExternal: contentSourceFilter === 'all' || contentSourceFilter === 'external',
      });
      setSessionStorageSummary(summary);
    } catch (err) {
      console.error('Failed to load session storage summary:', err);
      setSessionStorageSummary(null);
    }
  }, [token, showFilter, contentSourceFilter, visibilityFilter]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadSessions(searchTerm || undefined, activeFilter);
    }, searchTerm ? 300 : 0);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, activeFilter, showFilter, contentSourceFilter, visibilityFilter, loadSessions]);

  useEffect(() => {
    if (viewMode !== 'sessions') return;
    const timeoutId = setTimeout(() => {
      void loadSessionStorageSummary(searchTerm || undefined, activeFilter);
    }, searchTerm ? 300 : 0);

    return () => clearTimeout(timeoutId);
  }, [viewMode, searchTerm, activeFilter, showFilter, contentSourceFilter, visibilityFilter, loadSessionStorageSummary]);

  useEffect(() => {
    if (viewMode !== 'arrangements' || !token) return;
    loadArrangements().catch(err => console.error('Failed to load arrangements:', err));
  }, [viewMode, token, loadArrangements]);

  useEffect(() => {
    const handleStorageUpdated = () => {
      if (viewMode !== 'sessions') return;
      void loadSessions(searchTerm || undefined, activeFilter);
      void loadSessionStorageSummary(searchTerm || undefined, activeFilter);
    };

    window.addEventListener('rescript-session-storage-updated', handleStorageUpdated);
    return () => window.removeEventListener('rescript-session-storage-updated', handleStorageUpdated);
  }, [viewMode, searchTerm, activeFilter, loadSessions, loadSessionStorageSummary]);

  const formatDuration = (seconds: number): string => {
    if (!seconds) return '0s';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  const formatBytes = (bytes?: number): string => {
    const value = bytes || 0;
    if (value <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let size = value;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex += 1;
    }
    const decimals = unitIndex === 0 ? 0 : size >= 10 ? 1 : 2;
    return `${size.toFixed(decimals)} ${units[unitIndex]}`;
  };

  const formatMonthlyCost = (usd?: number): string => {
    const value = usd || 0;
    if (value <= 0) return '$0.00/mo';
    if (value < 0.01) return '<$0.01/mo';
    return `$${value.toFixed(value < 1 ? 2 : 1)}/mo`;
  };

  const getDisplayedMonthlyCost = (arrangement?: ArrangementListItem | null): number => {
    if (!arrangement) return 0;
    return arrangement.estimatedMonthlyTotalCostUsd ?? arrangement.estimatedMonthlyCostUsd ?? 0;
  };

  const handleSessionClick = (sessionId: string) => {
    onSessionSelect(sessionId);
  };

  const handleArrangementClick = (previewToken: string) => {
    window.location.assign(`/arrangement/${previewToken}`);
  };

  const showOptions = useMemo(() => {
    const items = new Map<string, string>();
    sessions.forEach((session) => {
      if (session.showSlug && session.showTitle) {
        items.set(session.showSlug, session.showTitle);
      }
    });
    return Array.from(items.entries())
      .map(([slug, title]) => ({ slug, title }))
      .sort((left, right) => left.title.localeCompare(right.title));
  }, [sessions]);

  const sessionStorageById = useMemo(
    () => new Map((sessionStorageSummary?.sessions || []).map((item) => [item.sessionId, item])),
    [sessionStorageSummary]
  );

  const displayItems = useMemo(() => {
    const groups = new Map<string, {
      key: string;
      family: SessionItem['family'];
      items: SessionItem[];
    }>();

    sessions.forEach((session) => {
      const familyKey = session.family?.slug && (session.family.versionCount || 0) > 1
        ? `family:${session.family.slug}`
        : `session:${session.sessionId}`;
      const existing = groups.get(familyKey);
      if (existing) {
        existing.items.push(session);
        return;
      }
      groups.set(familyKey, {
        key: familyKey,
        family: session.family || null,
        items: [session],
      });
    });

    return Array.from(groups.values())
      .map((group) => {
        const items = [...group.items].sort((left, right) => {
          const leftVersion = left.family?.versionNumber ?? Number.NEGATIVE_INFINITY;
          const rightVersion = right.family?.versionNumber ?? Number.NEGATIVE_INFINITY;
          if (leftVersion !== rightVersion) {
            return rightVersion - leftVersion;
          }
          const updatedDiff = new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime();
          if (updatedDiff !== 0) {
            return updatedDiff;
          }
          return new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime();
        });
        return {
          key: group.key,
          family: group.family,
          items,
          primary: items[0],
          latestUpdatedAt: Math.max(...items.map((item) => new Date(item.updatedAt).getTime())),
          storage: items.reduce<SessionStorageDigest>((acc, item) => {
            const storage = sessionStorageById.get(item.sessionId);
            if (!storage) return acc;
            acc.sourceBytes += storage.sourceBytes || 0;
            acc.proxyBytes += storage.proxyBytes || 0;
            acc.totalBytes += storage.totalBytes || 0;
            acc.estimatedMonthlyStorageCostUsd += storage.estimatedMonthlyStorageCostUsd || 0;
            acc.cleanupCandidateBytes += storage.proxyCleanupCandidate ? (storage.proxyBytes || 0) : 0;
            acc.cleanupCandidateCount += storage.proxyCleanupCandidate ? 1 : 0;
            acc.playableCount += storage.hasPlayableVideo ? 1 : 0;
            acc.hasPlayableVideo = acc.hasPlayableVideo || storage.hasPlayableVideo;
            acc.hasYoutubeVideo = acc.hasYoutubeVideo || storage.hasYoutubeVideo;
            acc.hasProxyVideo = acc.hasProxyVideo || storage.hasProxyVideo;
            acc.hasAwsSource = acc.hasAwsSource || storage.hasAwsSource;
            return acc;
          }, {
            sourceBytes: 0,
            proxyBytes: 0,
            totalBytes: 0,
            estimatedMonthlyStorageCostUsd: 0,
            playableCount: 0,
            cleanupCandidateCount: 0,
            cleanupCandidateBytes: 0,
            hasPlayableVideo: false,
            hasYoutubeVideo: false,
            hasProxyVideo: false,
            hasAwsSource: false,
          }),
        };
      })
      .sort((left, right) => right.latestUpdatedAt - left.latestUpdatedAt);
  }, [sessions, sessionStorageById]);

  useEffect(() => {
    if (showFilter !== 'all' && !showOptions.some((option) => option.slug === showFilter)) {
      setShowFilter('all');
    }
  }, [showFilter, showOptions]);

  const toggleFamilyExpanded = useCallback((familySlug: string) => {
    setExpandedFamilies((current) => {
      const next = new Set(current);
      if (next.has(familySlug)) {
        next.delete(familySlug);
      } else {
        next.add(familySlug);
      }
      return next;
    });
  }, []);


  const handleArrangementDeleteClick = (e: React.MouseEvent, arrangement: ArrangementListItem) => {
    e.preventDefault();
    e.stopPropagation();
    setArrangementToDelete(arrangement);
    setArrangementCacheModalOpen(true);
  };

  const handleArrangementCachePurgeConfirm = async () => {
    if (!arrangementToDelete || !token) return;

    try {
      setIsPurgingArrangementCache(true);
      await purgeArrangementCache(arrangementToDelete.id, token);
      await loadArrangements();
      setArrangementCacheModalOpen(false);
      setArrangementToDelete(null);
    } catch (err) {
      console.error('Failed to purge arrangement cache:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete cached files');
    } finally {
      setIsPurgingArrangementCache(false);
    }
  };

  const handleArrangementDeleteConfirm = async () => {
    if (!arrangementToDelete || !token) return;

    try {
      setIsDeletingArrangement(true);
      await deleteArrangement(arrangementToDelete.id, arrangementToDelete.title || arrangementToDelete.id, token);
      setArrangements(prev => prev.filter(arr => arr.id !== arrangementToDelete.id));
      setArrangementDeleteModalOpen(false);
      setArrangementCacheModalOpen(false);
      setArrangementToDelete(null);
    } catch (err) {
      console.error('Failed to delete arrangement:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete arrangement');
    } finally {
      setIsDeletingArrangement(false);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, session: SessionItem) => {
    e.stopPropagation();
    setSessionToDelete(session);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!sessionToDelete || !token) return;

    try {
      setIsDeleting(true);
      await sessionService.deleteSession(sessionToDelete.sessionId, token);
      setSessions(prev => prev.filter(s => s.sessionId !== sessionToDelete.sessionId));
      setDeleteModalOpen(false);
      setSessionToDelete(null);
    } catch (err) {
      console.error('Failed to delete session:', err);
      setError('Failed to delete session');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleDeprecationClick = (e: React.MouseEvent, session: SessionItem) => {
    e.stopPropagation();
    setSessionToToggleDeprecation(session);
    setDeprecationModalOpen(true);
  };

  const handleToggleDeprecationConfirm = async () => {
    if (!sessionToToggleDeprecation || !token) return;

    const deprecated = !sessionToToggleDeprecation.isDeprecated;
    const fallbackReason = deprecated ? 'Deprecated from the session browser' : null;

    try {
      setIsTogglingDeprecation(true);
      const result = await sessionService.toggleDeprecation(
        sessionToToggleDeprecation.sessionId,
        deprecated,
        deprecated ? (sessionToToggleDeprecation.deprecatedReason || fallbackReason) : null,
        token
      );

      setSessions((prev) => {
        if (deprecated) {
          return prev.filter((session) => session.sessionId !== sessionToToggleDeprecation.sessionId);
        }
        return prev.map((session) => (
          session.sessionId === sessionToToggleDeprecation.sessionId
            ? {
                ...session,
                title: result.session.title || session.title,
                isDeprecated: result.session.deprecated,
                deprecatedAt: result.session.deprecatedAt || null,
                deprecatedReason: result.session.deprecatedReason || null,
              }
            : session
        ));
      });

      if (activeFilter === 'deprecated') {
        await loadSessions(searchTerm || undefined, activeFilter);
      }

      setDeprecationModalOpen(false);
      setSessionToToggleDeprecation(null);
    } catch (err) {
      console.error('Failed to toggle session deprecation:', err);
      setError('Failed to update session deprecation');
    } finally {
      setIsTogglingDeprecation(false);
    }
  };

  const handleTogglePublish = async (e: React.MouseEvent, session: SessionItem) => {
    e.stopPropagation();
    if (!token) return;

    // Add to toggling set
    setTogglingPublish(prev => new Set(prev).add(session.sessionId));

    try {
      const newPublished = !session.publishedToHomepage;
      await sessionService.toggleHomepagePublish(session.sessionId, newPublished, token);

      // Update local state
      setSessions(prev => prev.map(s =>
        s.sessionId === session.sessionId
          ? { ...s, publishedToHomepage: newPublished }
          : s
      ));
    } catch (err) {
      console.error('Failed to toggle homepage publish:', err);
      setError('Failed to update homepage visibility');
    } finally {
      // Remove from toggling set
      setTogglingPublish(prev => {
        const next = new Set(prev);
        next.delete(session.sessionId);
        return next;
      });
    }
  };

  const renderSessionCard = (
    session: SessionItem,
    options: {
      nested?: boolean;
      familyVersionCount?: number;
      storage?: SessionStorageDigest | null;
    } = {}
  ) => {
    const isFamilyPrimary = !options.nested && (options.familyVersionCount || 0) > 1;
    const cardTitle = isFamilyPrimary && session.family?.title
      ? session.family.title
      : session.title;
    const showNonDefaultShow = !!session.showTitle && session.showSlug !== 'mlst';
    const storage = options.storage || null;
    const showHostedVideo = !!storage?.hasPlayableVideo;

    return (
      <motion.div
        key={session.sessionId}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="group cursor-pointer"
        onClick={() => handleSessionClick(session.sessionId)}
      >
        <div className={`${itemCardClassName} ${options.nested ? 'border-dashed dark:bg-[linear-gradient(180deg,rgba(29,39,54,0.96),rgba(23,31,45,0.96))]' : ''}`}>
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                {storage?.hasYoutubeVideo || session.videoSource === 'youtube' || session.isYouTubeMode ? (
                  <Youtube className="h-4 w-4 flex-shrink-0 text-red-500" />
                ) : showHostedVideo || session.videoSource === 'file' ? (
                  <Film className="h-4 w-4 flex-shrink-0 text-brand-yellow" />
                ) : session.videoSource === 'dropbox' ? (
                  <DropboxIcon size={16} className="flex-shrink-0 text-[#8AA0BD]" />
                ) : (
                  <Mic className="h-4 w-4 flex-shrink-0 text-brand-yellow" />
                )}
                <h4 className="truncate font-headlines text-base font-bold text-[#232E3F] dark:text-[#F4F4F4]">
                  {cardTitle}
                </h4>
                {session.family?.versionNumber != null && (
                  <span className={sessionChipClassNames.version}>
                    v{session.family.versionNumber}
                  </span>
                )}
                {options.familyVersionCount && options.familyVersionCount > 1 && !options.nested && (
                  <span className={sessionChipClassNames.count}>
                    {options.familyVersionCount} versions
                  </span>
                )}
                {storage && storage.totalBytes > 0 && (
                  <span className={sessionChipClassNames.gold}>
                    {formatBytes(storage.totalBytes)} media
                  </span>
                )}
                {storage?.cleanupCandidateCount ? (
                  <span className={sessionChipClassNames.danger}>
                    Proxy cleanup candidate
                  </span>
                ) : null}
                {showNonDefaultShow && (
                  <span className={sessionChipClassNames.show}>
                    {session.showTitle}
                  </span>
                )}
                {session.contentSource === 'external' && (
                  <span className={sessionChipClassNames.external}>
                    External
                  </span>
                )}
                {session.contentVisibility === 'internal' && (
                  <span className={sessionChipClassNames.neutral}>
                    Internal
                  </span>
                )}
                {session.isDeprecated && (
                  <div className={`flex flex-shrink-0 items-center gap-1 px-2 ${sessionChipClassNames.neutral}`} title={session.deprecatedReason || 'Deprecated session'}>
                    <Archive className="h-3 w-3 text-[#566377] dark:text-[#C6CDD8]" />
                    <span className="font-headlines text-[11px] font-semibold uppercase tracking-[0.1em] text-[#566377] dark:text-[#C6CDD8]">
                      Deprecated
                    </span>
                  </div>
                )}
                {session.shareLinksCount > 0 && (
                  <div className="flex flex-shrink-0 items-center gap-1 rounded-full bg-[#FFB52B]/[0.14] px-2 py-1 dark:bg-brand-yellow/15" title={`${session.shareLinksCount} public share link${session.shareLinksCount !== 1 ? 's' : ''}`}>
                    <Share2 className="h-3 w-3 text-[#C98A00] dark:text-brand-yellow" />
                    <span className="font-headlines text-[11px] font-semibold text-[#A36A00] dark:text-[#FFE3A1]">{session.shareLinksCount}</span>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-4 font-body text-xs text-[#6B788B] dark:text-[#D1D9E5]">
                <div className="flex items-center space-x-1">
                  <Hash className="h-3 w-3" />
                  <span>{truncateId(session.sessionId)}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Clock className="h-3 w-3" />
                  <span>{session.timeAgo}</span>
                </div>
                {session.speakerCount > 0 && (
                  <div className="flex items-center space-x-1">
                    <Users className="h-3 w-3" />
                    <span>{session.speakerCount} speaker{session.speakerCount !== 1 ? 's' : ''}</span>
                  </div>
                )}
                <div className="flex items-center space-x-1">
                  <FileText className="h-3 w-3" />
                  <span>{formatWordCount(session.wordCount)}</span>
                </div>
                {storage && storage.estimatedMonthlyStorageCostUsd > 0 && (
                  <div className="flex items-center space-x-1">
                    <Calendar className="h-3 w-3" />
                    <span>{formatMonthlyCost(storage.estimatedMonthlyStorageCostUsd)}</span>
                  </div>
                )}
              </div>

              {(session.speakerNames.length > 0 || session.tags.length > 0) && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {session.speakerNames.slice(0, 3).map((name, index) => (
                    <span
                      key={`${session.sessionId}-speaker-${index}`}
                      className={`inline-block ${sessionChipClassNames.speaker}`}
                    >
                      {name}
                    </span>
                  ))}
                  {session.speakerNames.length > 3 && (
                    <span className={`inline-block ${sessionChipClassNames.more}`}>
                      +{session.speakerNames.length - 3} more
                    </span>
                  )}
                  {session.tags.slice(0, 3).map((tag) => (
                    <span
                      key={`${session.sessionId}-tag-${tag}`}
                      className={`inline-block ${sessionChipClassNames.tag}`}
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="ml-4 flex flex-shrink-0 items-center space-x-2">
              <button
                onClick={(e) => handleToggleDeprecationClick(e, session)}
                className="rounded-full p-2 text-[#8E98A8] transition-all duration-200 opacity-0 group-hover:opacity-100 hover:bg-[#E8ECF1] hover:text-[#566377] dark:hover:bg-[#2A3445] dark:hover:text-[#C6CDD8]"
                title={session.isDeprecated ? 'Restore session to the main list' : 'Deprecate session and hide it from the main list'}
              >
                {session.isDeprecated ? (
                  <Undo2 className="h-4 w-4" />
                ) : (
                  <Archive className="h-4 w-4" />
                )}
              </button>
              {session.youtubeUrl && session.shareLinksCount > 0 && (
                <button
                  onClick={(e) => handleTogglePublish(e, session)}
                  disabled={togglingPublish.has(session.sessionId)}
                  className={`rounded-full p-2 transition-all duration-200 opacity-0 group-hover:opacity-100
                         ${session.publishedToHomepage
                           ? 'text-[#C98A00] hover:bg-[#FFF4D9] dark:text-brand-yellow dark:hover:bg-[#2B3547]'
                           : 'text-[#8E98A8] hover:bg-[#E8ECF1] hover:text-[#566377] dark:hover:bg-[#2A3445] dark:hover:text-[#C6CDD8]'
                         }
                         ${togglingPublish.has(session.sessionId) ? 'opacity-50 cursor-wait' : ''}`}
                  title={session.publishedToHomepage ? 'Published to homepage - click to hide' : 'Hidden from homepage - click to publish'}
                >
                  {togglingPublish.has(session.sessionId) ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Globe className={`h-4 w-4 ${session.publishedToHomepage ? '' : 'opacity-45'}`} />
                  )}
                </button>
              )}
              <button
                onClick={(e) => handleDeleteClick(e, session)}
                className="rounded-full p-2 text-[#8E98A8] hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400 transition-all duration-200 opacity-0 group-hover:opacity-100"
                title="Delete session"
              >
                <Trash2 className="h-4 w-4" />
              </button>
              <ArrowRight className="h-5 w-5 text-[#8E98A8] transition-all duration-200 group-hover:translate-x-1 group-hover:text-[#C98A00] dark:group-hover:text-brand-yellow" />
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  const formatWordCount = (count: number): string => {
    if (count < 1000) return `${count} words`;
    if (count < 1000000) return `${(count / 1000).toFixed(1)}k words`;
    return `${(count / 1000000).toFixed(1)}M words`;
  };

  const truncateId = (id: string): string => {
    return `${id.substring(0, 8)}...`;
  };

  if (isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={shellClassName}
      >
        <div className="flex items-center justify-center py-10">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-brand-yellow" />
            <span className="font-headlines text-sm font-semibold uppercase tracking-[0.18em] text-[#566377] dark:text-[#C6CDD8]">
              Loading sessions...
            </span>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={shellClassName}
    >
      {/* Header */}
      <div className={`border-b ${browserHeaderClassName}`}>
        <div className="flex flex-col gap-4 mb-4">
          <div className="flex items-start gap-3 px-4 pt-5 sm:px-6 sm:pt-6">
            <div className="flex-shrink-0 rounded-2xl border border-[#FFB52B]/[0.22] bg-[#FFB52B]/[0.12] p-2.5 shadow-[0_18px_32px_-24px_rgba(255,181,43,0.9)]">
              {viewMode === 'sessions' ? (
                <FileText size={20} className="text-brand-yellow" />
              ) : (
                <Film size={20} className="text-brand-yellow" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className={`font-headlines text-[10px] font-semibold uppercase tracking-[0.28em] ${headerEyebrowClassName}`}>
                Discovery
              </p>
              <h3 className={`font-headlines text-lg font-bold tracking-tight sm:text-[1.35rem] ${headerTitleClassName}`}>
                {viewMode === 'sessions' ? 'Previous Sessions' : 'Arrangements'}
              </h3>
              <p className={`mt-1 max-w-2xl font-body text-sm leading-6 ${headerBodyClassName}`}>
                {viewMode === 'sessions'
                  ? 'Browse and search your transcription sessions by name, speaker, or content'
                  : 'Browse your clip arrangements and show edits'}
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-3 px-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <div className={`grid w-full grid-cols-2 rounded-full p-1 sm:w-auto ${viewToggleClassName}`}>
              <button
                onClick={() => setViewMode('sessions')}
                className={`flex items-center justify-center gap-1.5 rounded-full px-4 py-2 font-headlines text-xs font-semibold uppercase tracking-[0.14em] transition-all duration-200 sm:text-sm ${
                  viewMode === 'sessions'
                    ? 'bg-[#FFB52B] text-[#232E3F] shadow-[0_18px_32px_-24px_rgba(255,181,43,0.88)]'
                    : inactiveViewModeClassName
                }`}
              >
                <FileText className="h-3.5 w-3.5" />
                Sessions
              </button>
              <button
                onClick={() => setViewMode('arrangements')}
                className={`flex items-center justify-center gap-1.5 rounded-full px-4 py-2 font-headlines text-xs font-semibold uppercase tracking-[0.14em] transition-all duration-200 sm:text-sm ${
                  viewMode === 'arrangements'
                    ? 'bg-[#FFB52B] text-[#232E3F] shadow-[0_18px_32px_-24px_rgba(255,181,43,0.88)]'
                    : inactiveViewModeClassName
                }`}
              >
                <Film className="h-3.5 w-3.5" />
                Arrangements
              </button>
            </div>
            <button
              onClick={() => navigate('/admin/comments')}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#FFB52B] px-4 py-2.5 font-headlines text-sm font-semibold text-[#232E3F] shadow-[0_20px_34px_-24px_rgba(255,181,43,0.9)] transition-all hover:-translate-y-0.5 hover:bg-[#FFC54D] sm:w-auto"
              title="Explore and manage comments"
            >
              <MessageSquare size={18} />
              <span>Comment Review</span>
            </button>
          </div>
        </div>

        {/* Search Bar - Sessions only */}
        {viewMode === 'sessions' && (
          <div className="mb-3 space-y-3 px-4 sm:px-6">
            <div className="relative">
              <Search className={`absolute left-7 top-1/2 h-4 w-4 -translate-y-1/2 ${searchIconClassName}`} />
              <input
                type="text"
                placeholder="Search transcripts, topics, speakers, comments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={searchInputClassName}
              />
              {isSearching && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  <Loader2 className="h-4 w-4 animate-spin text-brand-yellow" />
                </div>
              )}
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <select
                value={showFilter}
                onChange={(e) => setShowFilter(e.target.value)}
                className={selectInputClassName}
              >
                <option value="all">All shows</option>
                {showOptions.map((show) => (
                  <option key={show.slug} value={show.slug}>
                    {show.title}
                  </option>
                ))}
              </select>
              <select
                value={contentSourceFilter}
                onChange={(e) => setContentSourceFilter(e.target.value as ContentSourceFilter)}
                className={selectInputClassName}
              >
                <option value="owned">Owned content</option>
                <option value="all">Owned + external</option>
                <option value="external">External only</option>
              </select>
              <select
                value={visibilityFilter}
                onChange={(e) => setVisibilityFilter(e.target.value as ContentVisibilityFilter)}
                className={selectInputClassName}
              >
                <option value="all">All visibility</option>
                <option value="default">Default visibility</option>
                <option value="internal">Internal only</option>
              </select>
            </div>
          </div>
        )}

        {/* Filter Buttons - Sessions only */}
        {viewMode === 'sessions' && (
          <div className="flex flex-wrap items-center gap-2 px-4 pb-5 sm:px-6 sm:pb-6">
            <Filter className={`hidden h-4 w-4 sm:block ${filterIconClassName}`} />
            <button
              onClick={() => setActiveFilter('all')}
              className={getFilterButtonClassName('all')}
            >
              All
            </button>
            <button
              onClick={() => setActiveFilter('youtube')}
              className={getFilterButtonClassName('youtube')}
            >
              <Youtube className="h-3 w-3" />
              YouTube
            </button>
            <button
              onClick={() => setActiveFilter('video')}
              className={getFilterButtonClassName('video')}
            >
              <Film className="h-3 w-3" />
              Video
            </button>
            <button
              onClick={() => setActiveFilter('shared')}
              className={getFilterButtonClassName('shared')}
            >
              <Share2 className="h-3 w-3" />
              Shared
            </button>
            <button
              onClick={() => setActiveFilter('deprecated')}
              className={getFilterButtonClassName('deprecated')}
            >
              <EyeOff className="h-3 w-3" />
              Deprecated
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5 sm:p-6">
        {viewMode === 'arrangements' ? (
          /* Arrangements View */
          <>
            {isLoadingArrangements && (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="mr-3 h-5 w-5 animate-spin text-brand-yellow" />
                <span className="font-headlines text-sm font-semibold uppercase tracking-[0.18em] text-[#566377] dark:text-[#C6CDD8]">
                  Loading arrangements...
                </span>
              </div>
            )}
            {!isLoadingArrangements && arrangements.length === 0 && (
              <div className={`${subtleCardClassName} px-6 py-10 text-center`}>
                <Film className="mx-auto mb-4 h-12 w-12 text-brand-yellow" />
                <h4 className="mb-2 font-headlines text-lg font-bold text-[#232E3F] dark:text-[#F4F4F4]">No arrangements yet</h4>
                <p className="font-body text-[#566377] dark:text-[#C6CDD8]">Create an arrangement via the API to get started.</p>
              </div>
            )}
            {!isLoadingArrangements && arrangements.length > 0 && (
              <div className="space-y-3">
                <AnimatePresence>
                  {arrangements.map((arr) => (
                    <motion.div
                      key={arr.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="group block cursor-pointer"
                      onClick={() => handleArrangementClick(arr.previewToken)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleArrangementClick(arr.previewToken);
                        }
                      }}
                      role="link"
                      tabIndex={0}
                    >
                      <div className={itemCardClassName}>
                        <div className="flex items-start justify-between">
                          <div className="min-w-0 flex-1">
                            <div className="mb-2 flex items-center space-x-2">
                              <Scissors className="h-4 w-4 flex-shrink-0 text-brand-yellow" />
                              <h4 className="truncate font-headlines text-base font-bold text-[#232E3F] dark:text-[#F4F4F4]">
                                {arr.title || 'Untitled Arrangement'}
                              </h4>
                              <span className={`rounded-full px-2.5 py-1 font-headlines text-[11px] font-semibold uppercase tracking-[0.12em] ${
                                arr.status === 'published'
                                  ? 'bg-[#1F5135]/[0.16] text-[#2C8B54] dark:bg-[#1F5135]/30 dark:text-[#9BE2B7]'
                                  : arr.status === 'draft'
                                  ? 'bg-[#FFB52B]/[0.16] text-[#A36A00] dark:bg-[#FFB52B]/[0.16] dark:text-[#FFE3A1]'
                                  : 'bg-[#E8ECF1] text-[#566377] dark:bg-[#2A3445] dark:text-[#C6CDD8]'
                              }`}>
                                {arr.status}
                              </span>
                            </div>
                            {arr.description && (
                              <p className="mb-2 line-clamp-2 font-body text-sm text-[#566377] dark:text-[#C6CDD8]">
                                {arr.description}
                              </p>
                            )}
                            <div className="flex flex-wrap items-center gap-4 font-body text-xs text-[#6B788B] dark:text-[#A9B4C3]">
                              <div className="flex items-center space-x-1">
                                <Film className="h-3 w-3" />
                                <span>{arr.totalClips} clip{arr.totalClips !== 1 ? 's' : ''}</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Clock className="h-3 w-3" />
                                <span>{formatDuration(arr.totalDurationSeconds)}</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Calendar className="h-3 w-3" />
                                <span>{new Date(arr.updatedAt).toLocaleDateString()}</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Hash className="h-3 w-3" />
                                <span>{formatBytes(arr.totalBytes ?? arr.storageBytes)}</span>
                              </div>
                              {typeof arr.sharedSourceVideoBytes === 'number' && arr.sharedSourceVideoBytes > 0 && (
                                <div className="flex items-center space-x-1">
                                  <Mic className="h-3 w-3" />
                                  <span>Source {formatBytes(arr.sharedSourceVideoBytes)}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="ml-4 flex flex-shrink-0 items-start gap-3">
                            <div className="hidden text-right sm:block">
                              <div className="font-headlines text-xs font-semibold uppercase tracking-[0.12em] text-[#3F4E62] dark:text-[#F4F4F4]">
                                {formatBytes(arr.totalBytes ?? arr.storageBytes)}
                              </div>
                              <div className="font-body text-[11px] text-[#6B788B] dark:text-[#A9B4C3]">
                                {formatMonthlyCost(getDisplayedMonthlyCost(arr))}
                              </div>
                              {typeof arr.estimatedMonthlyDeliveryCostUsd === 'number' && arr.estimatedMonthlyDeliveryCostUsd > 0 && (
                                <div className="font-body text-[10px] text-[#7B8798] dark:text-[#98A5B7]">
                                  Storage {formatMonthlyCost(arr.estimatedMonthlyCostUsd)} + CDN {formatMonthlyCost(arr.estimatedMonthlyDeliveryCostUsd)}
                                </div>
                              )}
                            </div>
                            <button
                              onClick={(e) => handleArrangementDeleteClick(e, arr)}
                              className="rounded-full p-2 text-[#8E98A8] transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                              title="Delete cached files or arrangement"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                            <ExternalLink className="h-5 w-5 text-[#8E98A8] transition-all group-hover:translate-x-0.5 group-hover:text-[#C98A00] dark:group-hover:text-brand-yellow" />
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </>
        ) : (
          /* Sessions View */
          <>
            {error && (
              <div className={`${subtleCardClassName} px-6 py-10 text-center`}>
                <div className="mb-2 font-headlines text-sm font-semibold uppercase tracking-[0.18em] text-red-600 dark:text-red-400">
                  {error}
                </div>
                <button
                  onClick={() => loadSessions()}
                  className="font-headlines text-sm font-semibold text-[#C98A00] underline decoration-[#FFB52B]/60 underline-offset-4 hover:opacity-80 dark:text-brand-yellow"
                >
                  Try again
                </button>
              </div>
            )}

            {!error && sessions.length === 0 && (
              <div className={`${subtleCardClassName} px-6 py-10 text-center`}>
                <EmptySessionIcon className="mx-auto mb-4 h-12 w-12 text-brand-yellow" />
                <h4 className="mb-2 font-headlines text-lg font-bold text-[#232E3F] dark:text-[#F4F4F4]">
                  {emptySessionTitle}
                </h4>
                <p className="font-body text-[#566377] dark:text-[#C6CDD8]">
                  {emptySessionDescription}
                </p>
              </div>
            )}

            {!error && sessions.length > 0 && (
              <div className="space-y-3">
                {sessionStorageSummary && (
                  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                    <div className={`${subtleCardClassName} px-4 py-4`}>
                      <p className="font-headlines text-[10px] font-semibold uppercase tracking-[0.18em] text-[#A36A00] dark:text-[#FFCC66]">
                        Matching Session Media
                      </p>
                      <div className="mt-2 font-headlines text-xl font-bold text-[#232E3F] dark:text-[#F4F4F4]">
                        {formatBytes(sessionStorageSummary.summary.totalBytes)}
                      </div>
                      <p className="mt-1 font-body text-xs text-[#6B788B] dark:text-[#A9B4C3]">
                        {sessionStorageSummary.summary.totalSessions} matching sessions
                      </p>
                    </div>
                    <div className={`${subtleCardClassName} px-4 py-4`}>
                      <p className="font-headlines text-[10px] font-semibold uppercase tracking-[0.18em] text-[#355E9A] dark:text-[#BCD7FF]">
                        Monthly Storage
                      </p>
                      <div className="mt-2 font-headlines text-xl font-bold text-[#232E3F] dark:text-[#F4F4F4]">
                        {formatMonthlyCost(sessionStorageSummary.summary.estimatedMonthlyStorageCostUsd)}
                      </div>
                      <p className="mt-1 font-body text-xs text-[#6B788B] dark:text-[#A9B4C3]">
                        Source {formatBytes(sessionStorageSummary.summary.totalSourceBytes)} + proxy {formatBytes(sessionStorageSummary.summary.totalProxyBytes)}
                      </p>
                    </div>
                    <div className={`${subtleCardClassName} px-4 py-4`}>
                      <p className="font-headlines text-[10px] font-semibold uppercase tracking-[0.18em] text-[#2D7250] dark:text-[#9EE0BD]">
                        Playable Video
                      </p>
                      <div className="mt-2 font-headlines text-xl font-bold text-[#232E3F] dark:text-[#F4F4F4]">
                        {sessionStorageSummary.summary.sessionsWithPlayableVideo}
                      </div>
                      <p className="mt-1 font-body text-xs text-[#6B788B] dark:text-[#A9B4C3]">
                        {sessionStorageSummary.summary.sessionsWithYoutubeVideo} YouTube · {sessionStorageSummary.summary.sessionsWithProxyVideo} proxied
                      </p>
                    </div>
                    <div className={`${subtleCardClassName} px-4 py-4`}>
                      <p className="font-headlines text-[10px] font-semibold uppercase tracking-[0.18em] text-[#B54747] dark:text-[#FFB3B3]">
                        Cleanup Candidates
                      </p>
                      <div className="mt-2 font-headlines text-xl font-bold text-[#232E3F] dark:text-[#F4F4F4]">
                        {sessionStorageSummary.summary.proxyCleanupCandidateCount}
                      </div>
                      <p className="mt-1 font-body text-xs text-[#6B788B] dark:text-[#A9B4C3]">
                        {formatBytes(sessionStorageSummary.summary.proxyCleanupCandidateBytes)} proxy bytes look redundant with YouTube
                      </p>
                    </div>
                  </div>
                )}
                <AnimatePresence>
                  {displayItems.map((group) => {
                    const hiddenItems = group.items.slice(1);
                    const familySlug = group.family?.slug || null;
                    const isExpanded = familySlug ? expandedFamilies.has(familySlug) : false;

                    return (
                      <div key={group.key} className="space-y-2">
                        <div className="relative">
                          {group.family && group.items.length > 1 && (
                            <>
                              <div className="pointer-events-none absolute inset-x-2 top-3 h-full rounded-[24px] border border-[#D6DCE4] bg-white/55 dark:border-[#344052] dark:bg-[#18212E]/45" />
                              <div className="pointer-events-none absolute inset-x-1 top-1.5 h-full rounded-[24px] border border-[#D6DCE4] bg-white/40 dark:border-[#344052] dark:bg-[#151D29]/40" />
                            </>
                          )}
                          <div className="relative">
                            {renderSessionCard(group.primary, {
                              familyVersionCount: group.family?.versionCount || group.items.length,
                              storage: group.storage,
                            })}
                          </div>
                        </div>

                        {group.family && hiddenItems.length > 0 && (
                          <div className="flex items-center justify-between px-2">
                            <div className="min-w-0">
                              <p className="font-body text-xs text-[#6B788B] dark:text-[#A9B4C3]">
                                Most recent version stays on top. Expand to inspect older edits in this family.
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => toggleFamilyExpanded(group.family!.slug)}
                              className="inline-flex items-center gap-2 rounded-full border border-[#D6DCE4] bg-white/85 px-3 py-1.5 font-headlines text-[11px] font-semibold uppercase tracking-[0.12em] text-[#355E9A] transition hover:border-[#AFC5E6] hover:bg-[#EEF3FF] dark:border-[#344052] dark:bg-[#202A3A] dark:text-[#BCD7FF] dark:hover:border-[#4D6485] dark:hover:bg-[#26344A]"
                            >
                              {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                              {isExpanded ? 'Collapse stack' : `Show ${hiddenItems.length} older`}
                            </button>
                          </div>
                        )}

                        {group.family && hiddenItems.length > 0 && isExpanded && (
                          <div className="ml-5 space-y-2 border-l border-dashed border-[#D6DCE4] pl-4 dark:border-[#344052]">
                            {hiddenItems.map((session) => (
                              <div key={session.sessionId}>
                                {renderSessionCard(session, {
                                  nested: true,
                                  storage: (() => {
                                    const storage = sessionStorageById.get(session.sessionId);
                                    if (!storage) return null;
                                    return {
                                      sourceBytes: storage.sourceBytes || 0,
                                      proxyBytes: storage.proxyBytes || 0,
                                      totalBytes: storage.totalBytes || 0,
                                      estimatedMonthlyStorageCostUsd: storage.estimatedMonthlyStorageCostUsd || 0,
                                      playableCount: storage.hasPlayableVideo ? 1 : 0,
                                      cleanupCandidateCount: storage.proxyCleanupCandidate ? 1 : 0,
                                      cleanupCandidateBytes: storage.proxyCleanupCandidate ? (storage.proxyBytes || 0) : 0,
                                      hasPlayableVideo: !!storage.hasPlayableVideo,
                                      hasYoutubeVideo: !!storage.hasYoutubeVideo,
                                      hasProxyVideo: !!storage.hasProxyVideo,
                                      hasAwsSource: !!storage.hasAwsSource,
                                    };
                                  })(),
                                })}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </AnimatePresence>
              </div>
            )}

            {sessions.length >= 50 && (
              <div className="mt-4 text-center">
                <p className="font-body text-sm text-[#566377] dark:text-[#C6CDD8]">
                  Showing {sessions.length} most recent sessions{activeFilter !== 'all'
                    ? ` with ${
                      activeFilter === 'youtube'
                        ? 'YouTube links'
                        : activeFilter === 'video'
                          ? 'playable video'
                          : activeFilter === 'shared'
                            ? 'public shares'
                            : 'deprecated status'
                    }`
                    : ''}. Use search to find more.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSessionToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Session"
        message={`Are you sure you want to delete the session "${sessionToDelete?.title || sessionToDelete?.filename}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonClassName="bg-red-600 hover:bg-red-700"
        isLoading={isDeleting}
      />
      <ConfirmationModal
        isOpen={deprecationModalOpen}
        onClose={() => {
          setDeprecationModalOpen(false);
          setSessionToToggleDeprecation(null);
        }}
        onConfirm={handleToggleDeprecationConfirm}
        title={sessionToToggleDeprecation?.isDeprecated ? 'Restore Session' : 'Deprecate Session'}
        message={sessionToToggleDeprecation?.isDeprecated
          ? `Restore "${sessionToToggleDeprecation.title || sessionToToggleDeprecation.filename}" to the default session list?`
          : (
            <div className="space-y-2">
              <div>
                Deprecate "{sessionToToggleDeprecation?.title || sessionToToggleDeprecation?.filename}" and hide it from the default session list?
              </div>
              <div className="text-sm text-amber-700 dark:text-amber-300">
                Warning: once deprecated, this session disappears from the normal browser. Restoring it later requires switching to the deprecated filter first.
              </div>
            </div>
          )}
        confirmText={sessionToToggleDeprecation?.isDeprecated ? 'Restore' : 'Deprecate'}
        cancelText="Cancel"
        confirmButtonClassName={sessionToToggleDeprecation?.isDeprecated ? 'bg-[#2563eb] hover:bg-[#1d4ed8]' : 'bg-[#566377] hover:bg-[#3F4E62]'}
        isLoading={isTogglingDeprecation}
        confirmationValue={sessionToToggleDeprecation?.isDeprecated ? undefined : 'DEPRECATE'}
        confirmationLabel={sessionToToggleDeprecation?.isDeprecated ? undefined : 'Type DEPRECATE to continue'}
        confirmationPlaceholder={sessionToToggleDeprecation?.isDeprecated ? undefined : 'DEPRECATE'}
        confirmationHint={sessionToToggleDeprecation?.isDeprecated ? undefined : 'This is a high-friction action because the session will no longer appear in the default UI list.'}
      />
      <ConfirmationModal
        isOpen={arrangementCacheModalOpen}
        onClose={() => {
          setArrangementCacheModalOpen(false);
          setArrangementToDelete(null);
        }}
        onConfirm={handleArrangementCachePurgeConfirm}
        title="Delete Cached Files"
        message={
          <div className="space-y-2">
            <div>This removes generated clip audio/video, composite renders, show MP3 output, and local source cache while keeping the arrangement, its revisions, and uploaded visual assets.</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Current footprint: {formatBytes(arrangementToDelete?.totalBytes ?? arrangementToDelete?.storageBytes)} · {formatMonthlyCost(getDisplayedMonthlyCost(arrangementToDelete))}
            </div>
            <button
              type="button"
              onClick={() => {
                setArrangementCacheModalOpen(false);
                setArrangementDeleteModalOpen(true);
              }}
              className="inline-flex items-center rounded-md border border-red-300 px-3 py-1.5 text-sm font-medium text-red-700 transition-colors hover:bg-red-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950/30"
            >
              Delete Entire Arrangement Instead
            </button>
          </div>
        }
        confirmText="Delete Cached Files"
        cancelText="Cancel"
        confirmButtonClassName="bg-amber-600 hover:bg-amber-700"
        isLoading={isPurgingArrangementCache}
      />
      <ConfirmationModal
        isOpen={arrangementDeleteModalOpen}
        onClose={() => {
          setArrangementDeleteModalOpen(false);
          setArrangementToDelete(null);
        }}
        onConfirm={handleArrangementDeleteConfirm}
        title="Delete Arrangement"
        message={
          <div className="space-y-2">
            <div>This will permanently remove the arrangement, its generated media, and any orphaned source-video cache only used by this arrangement.</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Current footprint: {formatBytes(arrangementToDelete?.totalBytes ?? arrangementToDelete?.storageBytes)} · {formatMonthlyCost(getDisplayedMonthlyCost(arrangementToDelete))}
            </div>
          </div>
        }
        confirmText="Delete Arrangement"
        cancelText="Cancel"
        confirmButtonClassName="bg-red-600 hover:bg-red-700"
        isLoading={isDeletingArrangement}
        confirmationValue={arrangementToDelete?.title || arrangementToDelete?.id}
        confirmationLabel="Type the arrangement title to confirm"
      />
    </motion.div>
  );
};

export default SessionBrowser;
